import { z } from 'zod';

/**
 * Structured JSON schema we require Claude to return after reading an auction's
 * PDFs (appraiser report + seizure report + court decision if present).
 *
 * DECISION FLOW:
 * - ownership_percent comes first. If != 100, Claude returns `discard: true` and
 *   skips the heavy fields. We don't pay to extract data we're going to throw away.
 * - If ownership_percent == 100, Claude returns the full structured extraction.
 */

export const PropertyDetailsSchema = z.object({
  sqm: z.number().nullable(),
  floor: z.number().int().nullable(),
  rooms: z.number().int().nullable(),
  construction_year: z.number().int().nullable(),
  parking: z.boolean().nullable(),
  storage: z.boolean().nullable(),
  property_sub_type: z.string().nullable(),
});

export const ComparableSchema = z.object({
  address: z.string().nullable(),
  asking_price: z.number().nullable(),
  sqm: z.number().nullable(),
  price_per_sqm: z.number().nullable(),
  source: z.string().nullable(),
});

export const RedFlagSchema = z.object({
  flag: z.boolean(),
  detail: z.string().nullable(),
});

export const ClaudeAnalysisSchema = z.object({
  // Always present
  ownership_percent: z.number().int().min(0).max(100),
  ownership_type: z.enum(['full', 'bare', 'usufruct', 'shared', 'unknown']),
  discard: z.boolean(),
  discard_reason: z.string().nullable(),

  // Present only when discard == false (ownership_percent == 100)
  property_details: PropertyDetailsSchema.nullable(),
  appraised_value: z.number().nullable(),
  appraised_sqm_price: z.number().nullable(),
  kaek: z.string().nullable(),
  gps: z
    .object({ lat: z.number(), lng: z.number() })
    .nullable(),
  comparables: z.array(ComparableSchema).default([]),
  red_flags: z
    .object({
      unauthorized_construction: RedFlagSchema,
      multiple_owners_or_shared: RedFlagSchema,
      occupied_by_third_parties: RedFlagSchema,
      pending_litigation: RedFlagSchema,
      forest_archaeological_coastal: RedFlagSchema,
      sqm_discrepancy_permit_vs_actual: RedFlagSchema,
      outstanding_tax_debt: RedFlagSchema,
      other_issues: RedFlagSchema,
    })
    .nullable(),
  overall_risk: z
    .object({
      rating: z.enum(['green', 'yellow', 'red']),
      explanation: z.string(),
    })
    .nullable(),
});

export type ClaudeAnalysis = z.infer<typeof ClaudeAnalysisSchema>;
